<template>
    <div class="relative bg-white rounded-lgr border border-border flex flex-col overflow-hidden text-[#595959]" :class="containerClass">
        <button
            v-if="!assessmentEnabled && !perspectiveReport"
            class="absolute top-4 right-4 cursor-pointer"
            :disabled="isFetching"
            title="Refresh Perspective Report"
            @click="$emit('refresh')">
            <svg
                aria-hidden="true"
                class="w-4 h-4"
                :class="{ 'animate-spin': isFetching }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg">
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M 20,4 V 9 H 19.418 M 4.062,11 A 8.001,8.001 0 0 1 19.418,9 m 0,0 H 15 M 4,20 v -5 h 0.581 m 0,0 A 8.003,8.003 0 0 0 19.938,13 M 4.581,15 H 9"></path>
            </svg>
        </button>
        <div class="shrink-0">
            <ArchetypeDot v-if="perspectiveArchetype" :letters="perspectiveArchetype.letters" class="size-14" />
            <PerspectiveCircles v-else-if="hasPerspectiveResult" />
            <PerspectiveStyleCircles v-else />
        </div>
        <div class="flex flex-col w-full" :class="{ 'items-center': archetype }">
            <div v-if="archetype" class="details-big text-valence-grey-700">You're {{ articleFor(archetype) }}</div>
            <div :class="[titleClass, hasPerspectiveResult ? 'text-valence-grey-850' : 'text-ink']">
                {{ cardTitle }}
            </div>
            <div :class="hasPerspectiveResult ? 'details-big mt-0.5 text-valence-grey-700' : 'body text-pretty text-ink-muted'">
                {{ cardDescription }}
            </div>
        </div>
        <BaseButton theme="neutral" class="w-full" :class="{ 'mt-auto': hasPerspectiveResult }" @click="handleClick">{{ ctaButtonText }}</BaseButton>
    </div>
</template>

<script setup lang="ts">
import { usePage } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import { type PerspectiveReport } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import PerspectiveCircles from "~vue/components/PerspectiveCircles.vue";
import PerspectiveStyleCircles from "~vue/components/PerspectiveStyleCircles.vue";
import { PERSPECTIVE_TYPES } from "~vue/pages/perspectiveTypes";
import ArchetypeDot from "~vue/Perspective/ArchetypeDot.vue";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { articleFor } from "~vue/utils/stringUtils";
import { computed, onMounted, ref } from "vue";

interface Props {
    perspectiveReport?: PerspectiveReport | null;
    isFetching?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    perspectiveReport: null,
    isFetching: false,
});

defineEmits<{ refresh: [] }>();

const perspectiveReport = computed(() => props.perspectiveReport);

const page = usePage();
const assessmentEnabled = computed(() => !!page.props.perspective_assessment_enabled);
const assessmentCompleted = computed(() => !!page.props.perspective_assessment_completed);
const assessmentInProgress = computed(() => !!page.props.perspective_assessment_in_progress);
const hasPerspectiveResult = computed(() => assessmentCompleted.value || !!perspectiveReport.value || !!perspectiveArchetype.value);

const perspectivePath = computed(() => (assessmentEnabled.value ? "native" : "team_tools"));
const resumeState = computed(() => (assessmentEnabled.value && assessmentInProgress.value ? "resume" : "fresh"));
const eventProps = computed(() => ({
    has_report: hasPerspectiveResult.value,
    surface: "home_discover_bar",
    perspective_path: perspectivePath.value,
    resume_state: resumeState.value,
}));

onMounted(() => logUserInteraction(AmplitudeEventNames.PERSPECTIVE_CARD_SHOWN, eventProps.value));

const perspectiveArchetype = computed(() => page.props.perspective_archetype as { letters: string; title: string } | null);
const archetype = computed(() => perspectiveArchetype.value?.title ?? undefined);
const typeData = computed(() => {
    const letters = perspectiveArchetype.value?.letters;
    return letters ? (PERSPECTIVE_TYPES[letters] ?? null) : null;
});

const titleClass = computed(() => {
    if (archetype.value) {
        return "display-serif";
    }
    return hasPerspectiveResult.value ? "nadia-subtitle" : "title-body";
});

const containerClass = computed(() => {
    if (archetype.value) {
        return "items-center gap-2 pt-8 pb-6 px-5";
    }
    return hasPerspectiveResult.value ? "items-start gap-2 pt-13.75 pb-10 px-5" : "items-start justify-end gap-[15px] p-5 min-h-[305px]";
});

const cardTitle = computed(() => {
    if (archetype.value) {
        return archetype.value;
    }
    return hasPerspectiveResult.value ? "Your Archetype" : "Discover your working style";
});
const cardDescription = computed(() => {
    if (typeData.value) {
        return typeData.value.shortDescription;
    }
    if (hasPerspectiveResult.value) {
        return "Chat with Nadia to get personalized insights for your working style.";
    }
    return "Take your Perspective to see which one of 16 working styles fits you — and what it reveals about how you work.";
});

const perspectiveUrl = computed(() => {
    if (assessmentEnabled.value) {
        return assessmentCompleted.value ? "/perspective/results/" : "/perspective/start/";
    }
    return props.perspectiveReport ? "/coach/start/?script=perspective&chat_source=perspective_topic" : `${page.props.align_host}/perspective/questions`;
});

const ctaButtonText = computed(() => (hasPerspectiveResult.value ? "Dive Deeper" : "Discover my style"));

const dismissPayload = ref<{ interaction_type: string } | null>(null);
const { execute: saveDismissal } = useFetch("/api/accounts/ui-interactions", { immediate: false }).post(dismissPayload).json();

const handleClick = () => {
    logUserInteraction(AmplitudeEventNames.PERSPECTIVE_CARD_CLICKED, eventProps.value);

    if (hasPerspectiveResult.value) {
        dismissPayload.value = { interaction_type: "perspective_card_dismissed" };
        saveDismissal();
    } else if (assessmentEnabled.value && !assessmentInProgress.value) {
        logUserInteraction(AmplitudeEventNames.PERSPECTIVE_ASSESSMENT_STARTED, { assessment_type: "perspective", source: "home_discover_bar" });
    }

    if (assessmentEnabled.value || props.perspectiveReport) {
        openUrl(perspectiveUrl.value);
    } else {
        // "Complete" - open in new tab (external link to Team Toolkit)
        openUrl(perspectiveUrl.value, { target: "_blank" });
    }
};
</script>
